/* eslint-disable prettier/prettier */
import { ChevronDown, X } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

type Option = {
  label: string;
  value: string;
};

type MultiSelectProps = {
  options: Option[];
  value: Option[];
  onChange: (value: Option[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
};

export const CustomMultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  className = '',
  disabled = false,
  error = false,
  errorMessage = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isSelected = (val: string) => value.some((v) => v.value === val);

  const toggleSelect = (item: Option) => {
    if (isSelected(item.value)) {
      onChange(value.filter((v) => v.value !== item.value));
    } else {
      onChange([...value, item]);
    }
  };

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        className={`w-full flex flex-wrap items-center justify-between gap-2 px-3 py-[6.75px] border-[1.5px] rounded-lg text-sm
        transition focus:outline-none focus:ring-1
        ${
          disabled
            ? 'bg-gray-100 text-muted-foreground border-primary cursor-not-allowed'
            : error
            ? 'border-red-500 focus:ring-red-500'
            : isOpen
            ? 'ring-1 ring-primary border-primary'
            : 'bg-white border-secondary hover:border-primary'
        }`}
        disabled={disabled}
      >
        <div className="flex flex-wrap gap-1">
          {value.length > 0 ? (
            value.map((item) => (
              <span
                key={item.value}
                className="flex items-center gap-1 bg-primary text-white text-xs px-2 py-[2px] rounded-full"
              >
                {item.label}
                <X
                  size={12}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(value.filter((v) => v.value !== item.value));
                  }}
                />
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">{placeholder}</span>
          )}
        </div>
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 overflow-y-auto text-sm bg-white border rounded-lg shadow-md max-h-60">
          {options.map((item) => {
            const selected = isSelected(item.value);
            return (
              <div
                key={item.value}
                onClick={() => toggleSelect(item)}
                className={`px-3 py-2 cursor-pointer transition ${
                  selected
                    ? 'bg-primary text-white'
                    : 'hover:bg-secondary hover:text-white'
                }`}
              >
                {item.label}
              </div>
            );
          })}
        </div>
      )}

      {error && errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};
